import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { environment } from '@env/environment';
import { isNil } from 'lodash';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { AuthService } from '../auth.service';
import { ApiService } from './api.service';
import { EnergyMapBuilding } from '@app/shared/models/energiekarte/energy-map-building';
import { Buildings } from '@app/energy-guard/models/buildings';

@Injectable({
  providedIn: 'root',
})
export class ConsumersService extends ApiService {
  private readonly allowedEnergyDataKomKeys: string[] = [];
  private readonly noKomKeyMunicipality: string[] = [];

  constructor(
    public http: HttpClient,
    private readonly userMunicipalitiesService: UserMunicipalitiesService,
    private readonly authService: AuthService,
  ) {
    super(http);
    this.allowedEnergyDataKomKeys = environment.allowedMeterReadingKomKeys.split(';');
    this.noKomKeyMunicipality = environment.noKomKeyMunicipality.split(';');
  }

  public getByMunicipalityKey(municipalityKey: string): Observable<IBuildingEfficiencyData[]> {
    let url = `socrates/meters/api/views/ViewBuildingMetersExtendedInfo1?$filter= municipalityKey eq '${municipalityKey}'`;
    if (!this.noKomKeyMunicipality.includes(municipalityKey)) {
      url += ` and komKey in ('${this.allowedEnergyDataKomKeys.join("','")}')`;
    }
    return this.http.get<IBuildingEfficiencyData[]>(this.endpoint(url));
  }

  public getByMunicipalityKeyOData(municipalityKey: string): Observable<EnergyMapBuilding[]> {
    const filterKomKey = !this.noKomKeyMunicipality.includes(municipalityKey);
    const metersColumns = ['Guid', 'MeterNumber', 'ConnectionObjectNumber', 'DeviceId', 'MarketLocationKey', 'ObjectType'].join(',');
    const selectColumns = [
      'buildingTypeId',
      'connectionObjectNumber',
      'creationDate',
      'description',
      'district',
      'districtNumber',
      'floorArea',
      'gpsModifiedBy',
      'gpsModifyDate',
      'guid',
      'houseNumber',
      'houseNumberAdditive',
      'latitude',
      'locationInfo',
      'longitude',
      'modifyDate',
      'modifyUser',
      'municipality',
      'municipalityId',
      'municipalityKey',
      'name',
      'regionName',
      'street',
      'zipCode',
    ].join(',');
    const filterKomKeyText = `'${this.allowedEnergyDataKomKeys.join("','")}'`;
    let filterOption = `$filter=Meters/any(m:m/UsageEndDate eq null)`;
    let metersExpandFilterOption = `$filter=UsageEndDate eq null`;

    if (filterKomKey) {
      filterOption = `$filter=Meters/any(m:m/UsageEndDate eq null and m/KomKey in (${filterKomKeyText}))`;
      metersExpandFilterOption = `$filter=UsageEndDate eq null and KomKey in (${filterKomKeyText})`;
    }

    const metersExpand = `Meters(${metersExpandFilterOption};$expand=device($select=onboardingKey),EnergyType($select=Name),MeterType($select=Code);$select=${metersColumns})`;
    const efficiencyAnalysesExpand = `EfficiencyAnalyses($select=electricityColorStatus,heatColorStatus)`;
    const expandOption = `$expand=${efficiencyAnalysesExpand},${metersExpand}`;
    const selectOption = `$select=${selectColumns}`;
    const queryOptions = `${filterOption}&${expandOption}&${selectOption}`;
    const url = `socrates/meters/api/municipalities/${municipalityKey}/buildings?${queryOptions}`;
    return this.http.get<EnergyMapBuilding[]>(this.endpoint(url));
  }

  public updateBuilding(data: any): Observable<Buildings> {
    if (isNil(data.municipalityKey)) {
      const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
      data.municipalityKey = municipalityKey;
    }

    if (isNil(data.modifyUser)) {
      data.modifyUser = this.authService.currentUser.guid;
    }

    if (isNil(data.modifyDate)) {
      data.modifyDate = moment().utc();
    }

    return this.http.patch<Buildings>(this.endpoint(`socrates/meters/api/municipalities/${data.municipalityKey}/buildings/${data.guid}`), data);
  }

  public getByGuid(guid: string, municipalityKey: string): Observable<Buildings[]> {
    return this.http.get<Buildings[]>(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/buildings/${guid}?$top=1`));
  }
}
